import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import api, { apiErr } from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, Heart, Home, Briefcase, Coins, Brain, CloudRain, BatteryLow, MoreHorizontal } from "lucide-react";
import { StepFrame, PrimaryBtn, GhostBtn, Field, GREEN, RUST, ART_ZODIAC } from "../components/StepFrame";

const CONCERNS = [
    ["relationship", Heart, "Relationships", "Love, trust, deep connection"],
    ["family", Home, "Family", "Understanding, boundaries, healing"],
    ["career", Briefcase, "Career & Purpose", "Clarity, growth, direction"],
    ["financial", Coins, "Financial Stress", "Money mindset, stability"],
    ["anxiety", Brain, "Mental Peace", "Reduce anxiety, overthinking"],
    ["low_mood", CloudRain, "Low Mood", "Feeling flat, heavy or tired"],
    ["burnout", BatteryLow, "Burnout", "Rest, energy, recovery"],
    ["other", MoreHorizontal, "Something Else", "Your own situation, your words"],
];

const STEPS = ["concern", "about", "account"];

export default function Onboarding() {
    const { language, setUser } = useApp();
    const nav = useNavigate();
    const [step, setStep] = useState(0);
    const [data, setData] = useState({ age: false, name: "", concern: "", dob: "", assessment: false, terms: false, memory: false, email: "", password: "" });
    const [busy, setBusy] = useState(false);
    const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
    const cur = STEPS[step];
    const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
    const prev = () => setStep((s) => Math.max(s - 1, 0));

    const canNext = () => {
        if (cur === "concern") return !!data.concern;
        if (cur === "about") return data.age;
        if (cur === "account") return data.assessment && data.terms && data.email && data.password.length >= 6;
        return true;
    };

    const finish = async () => {
        setBusy(true);
        try {
            const { data: u } = await api.post("/auth/register", {
                email: data.email, password: data.password, name: data.name || "Friend",
                language, age_confirmed: true, terms_accepted: data.terms, memory_consent: data.memory,
            });
            setUser(u);
            await api.post("/assessment/start", { concern: data.concern, assessment_consent: data.assessment });
            if (data.dob) { try { await api.post("/astrology", { birth_date: data.dob }); } catch (e) { /* optional */ } }
            toast.success("You're all set. Let's begin.");
            nav("/assessment");
        } catch (e) {
            toast.error(apiErr(e.response?.data?.detail));
        }
        setBusy(false);
    };

    const meta = {
        concern: { title: "Let’s begin your journey", sub: "Choose your focus. You can explore more areas later.", quote: "“You are not lost. You are becoming.”" },
        about: { title: "A Few Things About You", sub: "Answer honestly. There are no right or wrong answers.", art: ART_ZODIAC, dark: true },
        account: { title: "Create Your Space", sub: "Your reflections stay private in your own account.", quote: "“A calmer mind. A clearer path. A brighter you.”" },
    }[cur];

    return (
        <StepFrame onBack={step > 0 ? prev : () => nav("/")} right={<button onClick={() => nav("/")} className="hover:underline">Exit</button>}
            stepLabel={`Step ${step + 1} of ${STEPS.length}`} progress={((step + 1) / STEPS.length) * 100}
            title={meta.title} subtitle={meta.sub} art={meta.art} artDark={meta.dark} quote={meta.quote}
            footer={<>
                {step > 0 && <GhostBtn data-testid="ob-back" onClick={prev}><ArrowLeft size={15} /> Back</GhostBtn>}
                {cur === "account"
                    ? <PrimaryBtn data-testid="ob-finish" onClick={finish} disabled={!canNext() || busy}>{busy ? "Creating…" : "Create & Begin"} <ArrowRight size={15} /></PrimaryBtn>
                    : <PrimaryBtn data-testid="ob-next" onClick={next} disabled={!canNext()}>Continue <ArrowRight size={15} /></PrimaryBtn>}
            </>}>

            {cur === "concern" && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {CONCERNS.map(([val, Icon, title, sub]) => {
                        const on = data.concern === val;
                        return (
                            <button key={val} data-testid={`ob-concern-${val}`} onClick={() => set("concern", val)}
                                className="rounded-xl p-4 text-center flex flex-col items-center gap-2 transition hover:-translate-y-0.5"
                                style={{ background: on ? "#EEF3EF" : "#FBF8F1", border: `1px solid ${on ? GREEN : `${GREEN}1F`}`, boxShadow: on ? `0 0 0 1px ${GREEN}` : "none" }}>
                                <Icon size={26} strokeWidth={1.4} style={{ color: RUST }} />
                                <span className="text-sm font-semibold leading-tight" style={{ color: GREEN }}>{title}</span>
                                <span className="text-[11px] leading-snug" style={{ color: "#6B766F" }}>{sub}</span>
                            </button>
                        );
                    })}
                </div>
            )}

            {cur === "about" && (
                <div className="rounded-2xl p-6 sm:p-8 space-y-6" style={{ background: "#FBF8F1", border: `1px solid ${GREEN}1A` }}>
                    <Field data-testid="ob-name" label="What should we call you?" value={data.name} onChange={(e) => set("name", e.target.value)} placeholder="Your first name (optional)" />
                    <ChoiceRow data-testid="age-confirm" on={data.age} onClick={() => set("age", !data.age)} label="I confirm I am 18 years or older" />
                    <Field data-testid="ob-dob" label="Date of birth (optional, for astrological insights)" type="date" value={data.dob} onChange={(e) => set("dob", e.target.value)} max={new Date().toISOString().slice(0, 10)}
                        hint="Adds your sun sign and a gentle 'Your Stars, Your Story' reflection to your report. Offered as self-reflection, never prediction." />
                </div>
            )}

            {cur === "account" && (
                <div className="grid lg:grid-cols-2 gap-6">
                    <div className="rounded-2xl p-6 space-y-4" style={{ background: "#FBF8F1", border: `1px solid ${GREEN}1A` }}>
                        <Field data-testid="ob-email" label="Email" type="email" value={data.email} onChange={(e) => set("email", e.target.value)} placeholder="you@example.com" />
                        <Field data-testid="ob-password" label="Password" type="password" value={data.password} onChange={(e) => set("password", e.target.value)} placeholder="At least 6 characters" />
                    </div>
                    <div className="space-y-2.5">
                        <ChoiceRow data-testid="ob-consent-assessment" on={data.assessment} onClick={() => set("assessment", !data.assessment)} label="I consent to my answers being used to create my personal reflection *" />
                        <ChoiceRow data-testid="ob-consent-terms" on={data.terms} onClick={() => set("terms", !data.terms)} label="I accept the Terms and Privacy Policy *" />
                        <ChoiceRow data-testid="ob-consent-memory" on={data.memory} onClick={() => set("memory", !data.memory)} label="Allow the AI companion to remember our conversations (optional)" />
                    </div>
                </div>
            )}
        </StepFrame>
    );
}

export const ChoiceRow = ({ on, label, onClick, round = true, ...p }) => (
    <button {...p} type="button" onClick={onClick} className="w-full flex items-center gap-3 text-left rounded-xl px-4 py-3.5 text-sm transition"
        style={{ background: on ? "#E9EFEA" : "#FBF8F1", border: `1px solid ${on ? `${GREEN}66` : `${GREEN}1F`}`, color: "#2A3A33" }}>
        <span className={`h-5 w-5 shrink-0 flex items-center justify-center ${round ? "rounded-full" : "rounded-md"}`}
            style={{ border: `1.5px solid ${on ? GREEN : `${GREEN}66`}`, background: on ? GREEN : "transparent" }}>
            {on && <Check size={12} strokeWidth={3} style={{ color: "#F5F0E6" }} />}
        </span>
        <span>{label}</span>
    </button>
);
